/**
 * Landing Configuration Page - Admin interface to edit landing page settings
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getLandingConfig, updateLandingConfig } from '@/services/landingConfigService';
import { LandingConfig, LandingFeature } from '@/types';
import { Button } from '@/components/ui';
import { LoadingState } from '@/components/shared/LoadingState';
import { toast } from 'sonner';

export default function LandingConfigPage() {
  const { t } = useTranslation();
  const { customClaims } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<LandingConfig | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await getLandingConfig();
      setConfig(data);
    } catch (error) {
      console.error('Error loading landing config:', error);
      toast.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);
      await updateLandingConfig(config);
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      console.error('Error saving landing config:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const updateFeature = (index: number, updates: Partial<LandingFeature>) => {
    if (!config) return;

    const newFeatures = [...config.features];
    newFeatures[index] = { ...newFeatures[index], ...updates };
    setConfig({ ...config, features: newFeatures });
  };

  if (loading) {
    return <LoadingState message="Cargando configuración..." />;
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <p className="text-ink-muted">No se pudo cargar la configuración</p>
      </div>
    );
  }

  // Check if user has admin permissions
  if (!customClaims || !['super_admin', 'franchise_owner', 'admin'].includes(customClaims.role)) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <p className="text-ink-muted">No tienes permisos para acceder a esta página</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-ink mb-2">
            Configuración de Landing Page
          </h1>
          <p className="text-ink-muted">
            Edita los elementos que aparecen en la página principal
          </p>
        </div>

        {/* Schedule Section */}
        <div className="bg-surface rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-ink mb-4">
            📅 Horario del Negocio
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Lunes - Viernes
              </label>
              <input
                type="text"
                value={config.schedule.weekdays}
                onChange={(e) => setConfig({
                  ...config,
                  schedule: { ...config.schedule, weekdays: e.target.value }
                })}
                className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus bg-surface-2 text-ink"
                placeholder="10:00 - 21:00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Sábado
              </label>
              <input
                type="text"
                value={config.schedule.saturday}
                onChange={(e) => setConfig({
                  ...config,
                  schedule: { ...config.schedule, saturday: e.target.value }
                })}
                className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus bg-surface-2 text-ink"
                placeholder="10:00 - 20:00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Domingo
              </label>
              <input
                type="text"
                value={config.schedule.sunday}
                onChange={(e) => setConfig({
                  ...config,
                  schedule: { ...config.schedule, sunday: e.target.value }
                })}
                className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus bg-surface-2 text-ink"
                placeholder="Cerrado"
              />
            </div>
          </div>
        </div>

        {/* Average Wait Time Section */}
        <div className="bg-surface rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-ink mb-4">
            ⏱️ Tiempo de Espera Media
          </h2>
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Tiempo de espera promedio
            </label>
            <input
              type="text"
              value={config.averageWaitTime}
              onChange={(e) => setConfig({ ...config, averageWaitTime: e.target.value })}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus bg-surface-2 text-ink"
              placeholder="15min"
            />
            <p className="text-sm text-ink-muted mt-1">
              Ejemplo: "15min", "20 minutos", "~15min"
            </p>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-surface rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-ink mb-4">
            ✨ Características (Cards Inferiores)
          </h2>
          <p className="text-sm text-ink-muted mb-4">
            Estas son las 4 cards que aparecen en la parte inferior de la landing. Edita el ícono y las claves de traducción.
          </p>
          <div className="space-y-4">
            {config.features.map((feature, index) => (
              <div key={index} className="border border-hairline rounded-lg p-4">
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-3xl">{feature.icon}</span>
                  <span className="text-sm font-medium text-ink-muted">
                    Card #{feature.order}
                  </span>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-ink-muted mb-1">
                      Ícono (emoji)
                    </label>
                    <input
                      type="text"
                      value={feature.icon}
                      onChange={(e) => updateFeature(index, { icon: e.target.value })}
                      className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus bg-surface-2 text-ink text-center text-2xl"
                      placeholder="🐵"
                      maxLength={4}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-muted mb-1">
                      Clave del Título
                    </label>
                    <input
                      type="text"
                      value={feature.titleKey}
                      onChange={(e) => updateFeature(index, { titleKey: e.target.value })}
                      className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus bg-surface-2 text-ink text-sm font-mono"
                      placeholder="home.features.fast.title"
                    />
                    <p className="text-xs text-ink-muted mt-1">
                      {t(feature.titleKey)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-muted mb-1">
                      Clave de Descripción
                    </label>
                    <input
                      type="text"
                      value={feature.descriptionKey}
                      onChange={(e) => updateFeature(index, { descriptionKey: e.target.value })}
                      className="w-full px-3 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus bg-surface-2 text-ink text-sm font-mono"
                      placeholder="home.features.fast.description"
                    />
                    <p className="text-xs text-ink-muted mt-1">
                      {t(feature.descriptionKey)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button
            onClick={() => window.history.back()}
            variant="ghost"
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            isLoading={saving}
            className="bg-gradient-to-r from-accent to-accent hover:from-accent hover:to-accent"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </div>
    </div>
  );
}
